<script setup lang="ts">
  import { ref } from 'vue'
  import { useRouter } from 'vue-router'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { genericErrorToast } from '@/lib/toasts'
  import { useToast } from '@/components/ui/toast'
  import {
    FilePen,
    FileIcon,
    Download,
    Trash2,
    Plus,
    Copy,
    Eye,
    Ellipsis,
    Clock,
    Link2
  } from 'lucide-vue-next'
  import Button from '@/components/ui/button/Button.vue'
  import UserAvatar from '@/components/app/users/UserAvatar.vue'
  import { PdfFormTemplatesService, type PDFFormTemplate } from '@/types/generated'
  import PaginatedTableView, {
    type FetchDataOptions,
    type PaginatedResponse,
    type Filter
  } from '@/components/admin/PaginatedTable.vue'
  import { TableCell, TableRow } from '@/components/ui/table'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
  } from '@/components/ui/alert-dialog'
  import { ROUTES } from '@/router/routes'
  import NewPdfViewer from '@/components/pdf/NewPdfViewer.vue'

  const router = useRouter()
  const { toast } = useToast()
  const { t } = useTranslation({
    en: {
      intro:
        'Manage your PDF form templates. Here you can view, create, edit, and delete your form templates.',
      loading: 'Loading form templates...',
      error: 'Error loading form templates',
      noTemplates: 'No PDF form templates found',
      name: 'Name',
      createdBy: 'Created by',
      createdAt: 'Created At',
      preview: 'Preview',
      edit: 'Edit',
      duplicate: 'Duplicate',
      duplicateSuccess: 'Template duplicated successfully',
      download: 'Download',
      delete: 'Delete',
      create: 'Create Template',
      deleteSuccess: 'Template deleted successfully',
      deleteError: 'Error deleting template',
      downloadError: 'Download Error',
      downloadErrorDescription: 'No PDF file available',
      sortBy: 'Sort by',
      newestFirst: 'Newest first',
      oldestFirst: 'Oldest first',
      recentlyModified: 'Recently modified',
      leastRecentlyModified: 'Least recently modified',
      nameAZ: 'Name A-Z',
      nameZA: 'Name Z-A',
      deleteConfirmTitle: 'Delete Template',
      deleteConfirmDescription: 'Are you sure you want to delete the template',
      deleteConfirmAction: 'Delete',
      cancel: 'Cancel',
      copyLink: 'Copy Link',
      copySuccess: 'Link copied',
      copySuccessDescription: 'The link has been copied to the clipboard'
    },
    de: {
      intro:
        'Verwalten Sie Ihre PDF-Formularvorlagen. Hier können Sie Vorlagen anzeigen, erstellen, bearbeiten und löschen.',
      loading: 'Lade Formularvorlagen...',
      error: 'Fehler beim Laden der Formularvorlagen',
      noTemplates: 'Keine PDF-Formularvorlagen gefunden',
      name: 'Name',
      createdBy: 'Erstellt von',
      createdAt: 'Erstellt am',
      preview: 'Vorschau',
      edit: 'Bearbeiten',
      duplicate: 'Duplizieren',
      duplicateSuccess: 'Vorlage erfolgreich dupliziert',
      download: 'Herunterladen',
      delete: 'Löschen',
      create: 'Vorlage erstellen',
      deleteSuccess: 'Vorlage erfolgreich gelöscht',
      deleteError: 'Fehler beim Löschen der Vorlage',
      downloadError: 'Download-Fehler',
      downloadErrorDescription: 'Keine PDF-Datei verfügbar',
      sortBy: 'Sortieren nach',
      newestFirst: 'Neueste zuerst',
      oldestFirst: 'Älteste zuerst',
      recentlyModified: 'Kürzlich geändert',
      leastRecentlyModified: 'Am wenigsten geändert',
      nameAZ: 'Name A-Z',
      nameZA: 'Name Z-A',
      deleteConfirmTitle: 'Vorlage löschen',
      deleteConfirmDescription: 'Sind Sie sicher, dass Sie die Vorlage',
      deleteConfirmAction: 'Löschen',
      cancel: 'Abbrechen',
      copyLink: 'Link kopieren',
      copySuccess: 'Link kopiert',
      copySuccessDescription: 'Der Link wurde in die Zwischenablage kopiert'
    }
  })

  const paginatedTableRef = ref<InstanceType<typeof PaginatedTableView> | null>(null)
  const showDeleteDialog = ref(false)
  const templateToDelete = ref<PDFFormTemplate | null>(null)
  const showPdfViewer = ref(false)
  const previewTemplate = ref<PDFFormTemplate | null>(null)

  const filterableFields: Filter[] = [
    {
      key: 'ordering',
      label: t('sortBy'),
      type: 'select',
      value: '-created_at',
      options: [
        { label: t('newestFirst'), value: '-created_at' },
        { label: t('oldestFirst'), value: 'created_at' },
        { label: t('recentlyModified'), value: '-updated_at' },
        { label: t('leastRecentlyModified'), value: 'updated_at' },
        { label: t('nameAZ'), value: 'name' },
        { label: t('nameZA'), value: '-name' }
      ]
    }
  ]

  // Fetch data function for PaginatedTable
  const fetchTemplates = async (
    options?: FetchDataOptions
  ): Promise<PaginatedResponse<PDFFormTemplate>> => {
    try {
      // Get ordering from filters
      const orderingFilter = options?.filters?.find((f) => f.key === 'ordering')
      const ordering = orderingFilter?.value || '-created_at'

      const response = await PdfFormTemplatesService.pdfFormTemplatesList({
        limit: options?.limit,
        offset: options?.offset,
        search: options?.search,
        ordering: ordering
      })

      return {
        results: response.results,
        count: response.results.length, // Adjust count to reflect filtered results
        next: response.next || null,
        previous: response.previous || null
      }
    } catch (error) {
      console.error('Error loading templates:', error)
      genericErrorToast()
      return {
        results: [],
        count: 0,
        next: null,
        previous: null
      }
    }
  }

  const deleteTemplate = (template: PDFFormTemplate) => {
    templateToDelete.value = template
    showDeleteDialog.value = true
  }

  const confirmDelete = async () => {
    if (!templateToDelete.value) return

    try {
      await PdfFormTemplatesService.pdfFormTemplatesDestroy({ uuid: templateToDelete.value.uuid })
      if (paginatedTableRef.value) {
        paginatedTableRef.value.refreshData()
      }
      toast({
        title: t('deleteSuccess'),
        variant: 'default'
      })
    } catch (error) {
      console.error('Failed to delete template:', error)
      toast({
        title: t('deleteError'),
        variant: 'destructive'
      })
    } finally {
      showDeleteDialog.value = false
      templateToDelete.value = null
    }
  }

  const cancelDelete = () => {
    showDeleteDialog.value = false
    templateToDelete.value = null
  }

  // Format date
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const navigateToCreate = () => {
    router.push(ROUTES.APP.SETTINGS.PDF_FORM_TEMPLATES.PDF_FORM_TEMPLATES_CREATE)
  }

  const navigateToEdit = (template: PDFFormTemplate) => {
    router.push(
      `${ROUTES.APP.SETTINGS.PDF_FORM_TEMPLATES.PDF_FORM_TEMPLATES_EDIT.replace(':uuid', template.uuid)}`
    )
  }

  const openPreview = (template: PDFFormTemplate) => {
    previewTemplate.value = template
    showPdfViewer.value = true
  }

  const downloadTemplate = (template: PDFFormTemplate) => {
    const fileUrl = template.processed_pdf || template.original_pdf
    if (fileUrl) {
      window.open(fileUrl, '_blank')
    } else {
      toast({
        title: t('downloadError'),
        description: t('downloadErrorDescription'),
        variant: 'destructive'
      })
    }
  }

  const duplicateTemplate = async (template: PDFFormTemplate) => {
    try {
      await PdfFormTemplatesService.pdfFormTemplatesCopyCreate({
        uuid: template.uuid
      })
      toast({
        title: t('duplicateSuccess'),
        variant: 'default'
      })
      if (paginatedTableRef.value) {
        paginatedTableRef.value.refreshData()
      }
    } catch (error) {
      console.error('Failed to duplicate template:', error)
      genericErrorToast()
    }
  }

  const copyLink = (template: PDFFormTemplate) => {
    let link = `${window.location.origin}/client/portal/standalone/pdf-templates/${template.template_creation_token}`
    navigator.clipboard.writeText(link)
    toast({
      title: t('copySuccess'),
      description: t('copySuccessDescription'),
      variant: 'default'
    })
  }
</script>

<template>
  <SettingsPage>
    <template #actions>
      <Button
        data-cy="PDFFormTemplatesCreateButton"
        class="flex items-center gap-2"
        @click="navigateToCreate"
      >
        <Plus class="h-4 w-4" />
        {{ t('create') }}
      </Button>
    </template>

    <div class="space-y-4">
      <PaginatedTableView
        ref="paginatedTableRef"
        :fetchData="fetchTemplates"
        searchableFields="name,description"
        :filterableFields="filterableFields"
      >
        <template #table-head>
          <TableCell class="py-2 font-medium">{{ t('name') }}</TableCell>
          <TableCell class="w-48 py-2 font-medium">{{ t('createdBy') }}</TableCell>
          <TableCell class="w-40 py-2 font-medium">{{ t('createdAt') }}</TableCell>
          <TableCell class="w-12 py-2"></TableCell>
        </template>
        <template #table-row="{ items }">
          <TableRow
            v-for="template in items"
            :key="template.uuid"
            class="cursor-pointer hover:bg-gray-50"
            @click="navigateToEdit(template)"
          >
            <!-- Name column -->
            <TableCell class="py-4">
              <div class="flex items-center justify-between">
                <div class="flex min-w-0 flex-1 items-center gap-2">
                  <FileIcon class="h-5 w-5 flex-shrink-0 text-gray-400" />
                  <div class="min-w-0 flex-1">
                    <span class="block truncate text-sm font-medium text-gray-900">
                      {{ template.name }}
                    </span>
                    <span
                      v-if="template.description"
                      class="block truncate text-xs text-gray-500"
                    >
                      {{ template.description }}
                    </span>
                  </div>
                </div>
              </div>
            </TableCell>

            <!-- Created By column -->
            <TableCell class="py-4 text-sm text-gray-500">
              <div
                v-if="template.created_by"
                class="flex items-center gap-2"
              >
                <UserAvatar
                  :user="template.created_by"
                  size="24px"
                />
                <span class="text-gray-800">
                  {{ template.created_by.first_name }} {{ template.created_by.last_name }}
                </span>
              </div>
              <span
                v-else
                class="text-gray-400"
              >
                -
              </span>
            </TableCell>

            <!-- Created date column -->
            <TableCell class="py-4 text-sm text-gray-500">
              <div class="flex items-center gap-1">
                <Clock class="h-4 w-4" />
                {{ formatDate(template.created_at) }}
              </div>
            </TableCell>

            <!-- Actions column -->
            <TableCell class="py-4">
              <div class="flex items-center justify-end">
                <DropdownMenu>
                  <DropdownMenuTrigger as-child>
                    <Button
                      variant="ghost"
                      size="sm"
                      class="h-8 w-8 p-0"
                      data-cy="PDFFormTemplatesActionsButton"
                      @click.stop
                    >
                      <Ellipsis class="h-4 w-4" />
                      <span class="sr-only">Open menu</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    class="w-48"
                  >
                    <DropdownMenuItem
                      class="cursor-pointer"
                      data-cy="PDFFormTemplatesPreviewAction"
                      @click="openPreview(template)"
                    >
                      <Eye class="mr-2 h-4 w-4" />
                      {{ t('preview') }}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      class="cursor-pointer"
                      data-cy="PDFFormTemplatesEditAction"
                      @click="navigateToEdit(template)"
                    >
                      <FilePen class="mr-2 h-4 w-4" />
                      {{ t('edit') }}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      class="cursor-pointer"
                      data-cy="PDFFormTemplatesCopyLinkAction"
                      @click="copyLink(template)"
                    >
                      <Link2 class="mr-2 h-4 w-4" />
                      {{ t('copyLink') }}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      class="cursor-pointer"
                      data-cy="PDFFormTemplatesDuplicateAction"
                      @click="duplicateTemplate(template)"
                    >
                      <Copy class="mr-2 h-4 w-4" />
                      {{ t('duplicate') }}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      class="cursor-pointer"
                      data-cy="PDFFormTemplatesDownloadAction"
                      @click="downloadTemplate(template)"
                    >
                      <Download class="mr-2 h-4 w-4" />
                      {{ t('download') }}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      class="cursor-pointer text-red-600 focus:text-red-600"
                      data-cy="PDFFormTemplatesDeleteAction"
                      @click="deleteTemplate(template)"
                    >
                      <Trash2 class="mr-2 h-4 w-4" />
                      {{ t('delete') }}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </TableCell>
          </TableRow>
        </template>
      </PaginatedTableView>
    </div>

    <!-- Delete Confirmation Dialog -->
    <AlertDialog v-model:open="showDeleteDialog">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{{ t('deleteConfirmTitle') }}</AlertDialogTitle>
          <AlertDialogDescription>
            {{ t('deleteConfirmDescription') }} "{{ templateToDelete?.name }}"?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel @click="cancelDelete">
            {{ t('cancel') }}
          </AlertDialogCancel>
          <AlertDialogAction
            class="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            @click="confirmDelete"
          >
            {{ t('deleteConfirmAction') }}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <!-- PDF Preview Modal -->
    <NewPdfViewer
      v-model:isOpen="showPdfViewer"
      :url="previewTemplate?.processed_pdf || previewTemplate?.original_pdf || ''"
      :filename="previewTemplate?.name || 'PDF Template'"
      :initial-page="1"
      :annotation="false"
      :form-fields="previewTemplate?.form_fields || []"
      :show-form-fields="true"
      :disable-edit-page="true"
      :hide-topbar="true"
    >
      <template #customToolbar>
        <div class="flex items-center space-x-3">
          <Button
            variant="secondary"
            size="sm"
            @click="navigateToEdit(previewTemplate!)"
          >
            <FilePen class="mr-2 h-5 w-5" />
            {{ t('edit') }}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            @click="downloadTemplate(previewTemplate!)"
          >
            <Download class="mr-2 h-5 w-5" />
            {{ t('download') }}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            @click="copyLink(previewTemplate!)"
          >
            <Link2 class="mr-2 h-5 w-5" />
            {{ t('copyLink') }}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            @click="deleteTemplate(previewTemplate!)"
          >
            <Trash2 class="mr-2 h-4 w-4" />
            {{ t('delete') }}
          </Button>
        </div>
      </template>
    </NewPdfViewer>
  </SettingsPage>
</template>
